import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { gateway } from '../lib/gateway';
import { voice } from '../lib/voice';
import { clearCredentials } from '../lib/platform';
import { useStore } from '../store';

interface Device {
  id: string;
  label: string;
  createdAt: number;
  lastSeen: number;
  current: boolean;
}

/**
 * Sem senha, a credencial e o token guardado em cada navegador. Esta tela e o
 * equivalente ao "gerenciar sessoes": se um computador for perdido ou alguem
 * esquecer a sessao aberta, da para cortar so aquele acesso.
 */
export function DevicesModal({ onClose }: { onClose: () => void }) {
  const toast = useStore((s) => s.toast);
  const serverUrl = useStore((s) => s.serverUrl);
  const reset = useStore((s) => s.reset);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    void api
      .devices()
      .then((result) => setDevices(result.devices))
      .catch((error: Error) => toast(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  async function revoke(id: string) {
    if (!window.confirm('Cortar o acesso deste dispositivo?')) return;
    try {
      await api.revokeDevice(id);
      setDevices((current) => current.filter((d) => d.id !== id));
      toast('Acesso revogado.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao revogar.', 'error');
    }
  }

  async function changePassword() {
    setSavingPassword(true);
    try {
      await api.changePassword({
        currentPassword: currentPassword || undefined,
        password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      toast('Senha atualizada.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Falha ao trocar a senha.', 'error');
    } finally {
      setSavingPassword(false);
    }
  }

  async function signOut() {
    if (!window.confirm('Sair desta conta neste navegador?')) return;
    await voice.leave(true).catch(() => undefined);
    await api.logout().catch(() => undefined);
    gateway.disconnect();
    clearCredentials();
    reset();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Acesso e dispositivos</h2>
          <button className="icon-btn" onClick={onClose}>
            {'✕'}
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label>Servidor conectado</label>
            <div className="list-row">
              <span className="grow mono">{serverUrl}</span>
            </div>
          </div>

          <div className="member-group-title" style={{ paddingLeft: 0 }}>
            Trocar senha
          </div>
          <div className="field">
            <label>Senha atual</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            <div className="hint">Deixe em branco se voce ainda nao tem senha definida.</div>
          </div>
          <div className="field">
            <label>Nova senha</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newPassword.length >= 8 && void changePassword()}
              autoComplete="new-password"
            />
          </div>
          <button
            className="btn small"
            disabled={savingPassword || newPassword.length < 8}
            onClick={() => void changePassword()}
          >
            {savingPassword ? 'Salvando...' : 'Salvar senha'}
          </button>

          <div className="member-group-title" style={{ paddingLeft: 0, marginTop: 20 }}>
            Dispositivos com acesso
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Cada navegador guarda a propria chave de acesso. Nao ha senha nem e-mail para vazar, e
            revogar aqui derruba a sessao na hora.
          </p>

          {loading && <div style={{ color: 'var(--text-dim)' }}>Carregando...</div>}
          {devices.map((device) => (
            <div key={device.id} className="list-row">
              <div className="grow">
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {device.label}
                  {device.current && (
                    <span style={{ color: 'var(--green)', fontSize: 11, marginLeft: 8 }}>
                      este aqui
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  visto por ultimo em {new Date(device.lastSeen).toLocaleString('pt-BR')}
                </div>
              </div>
              {!device.current && (
                <button className="btn danger small" onClick={() => void revoke(device.id)}>
                  Revogar
                </button>
              )}
            </div>
          ))}

          <div className="member-group-title" style={{ paddingLeft: 0, marginTop: 20 }}>
            Sair
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Encerra a sessao deste navegador. Voce volta com apelido e senha.
          </p>
          <button className="btn danger small" onClick={() => void signOut()}>
            Sair da conta
          </button>
        </div>
      </div>
    </div>
  );
}
